import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
  Landmark,
  Building2,
  Cpu,
  Sparkles,
  Users,
  LineChart,
  Handshake,
  Lightbulb,
  Target,
  HardHat,
  Map,
  DraftingCompass,
} from "lucide-react";
import {
  NA_PRIMARY,
  NA_SECONDARY,
  naReadableText,
  naGradientLine,
  naDarkCardBase,
  onDarkCardEnter,
  onDarkCardLeave,
} from "./naSectionTokens";

const GOVERNMENT = [
  "Smart city leaders",
  "Urban planning authorities",
  "Transportation agencies",
  "Infrastructure departments",
  "Airport & transit authorities",
];

const PRIVATE = [
  "Developers & asset owners",
  "Engineering & architecture firms",
  "Construction companies",
  "Digital engineering teams",
  "GIS & geospatial specialists",
  "Technology providers & startups",
];

const WHY_ATTEND = [
  "Learn from real-world smart city and infrastructure case studies",
  "Connect with government and industry decision-makers",
  "Discover AI-enabled digital twin solutions",
  "Explore transportation and urban innovation projects",
  "Build partnerships across the infrastructure ecosystem",
  "Understand practical implementation strategies and ROI",
];

function AttendeeRow({ label, icon: Icon, color }) {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;
  return (
    <Card
      className="mb-2"
      style={{
        ...naDarkCardBase,
        borderRadius: "12px",
        padding: size ? "0.95rem 1.15rem" : "0.85rem 1rem",
        transition: "all 0.35s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        onDarkCardEnter(e, color);
        e.currentTarget.style.transform = "translateX(4px)";
      }}
      onMouseLeave={(e) => {
        onDarkCardLeave(e);
        e.currentTarget.style.transform = "translateX(0)";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
        <div
          style={{
            width: 38,
            height: 38,
            minWidth: 38,
            borderRadius: "10px",
            background: `${color}26`,
            border: `1px solid ${color}55`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={19} strokeWidth={2} style={{ color }} />
        </div>
        <span
          className="text-white"
          style={{
            fontSize: size ? "0.95rem" : "0.88rem",
            fontWeight: 600,
            lineHeight: 1.35,
            ...naReadableText,
          }}
        >
          {label}
        </span>
      </div>
    </Card>
  );
}

function NAWhoWhyAttendSection() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

  return (
    <section className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="justify-content-center mb-5">
          <Col xs={12}>
            <img
              src={require("assets/images/IMG_3375.png")}
              alt="Attendees"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "280px",
                objectFit: "cover",
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
              }}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-4">
          <Col className="text-center">
            <h2
              className="mb-0"
              style={{
                fontSize: size ? "2.25rem" : "1.85rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                lineHeight: 1.15,
                textTransform: "uppercase",
                color: NA_PRIMARY,
                ...naReadableText,
              }}
            >
              Who will attend
            </h2>
            <div
              style={{
                width: "110px",
                height: "4px",
                borderRadius: "2px",
                margin: "1rem auto 1.75rem",
                ...naGradientLine,
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={6} className="mb-4 mb-lg-0">
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "1.35rem" : "1.2rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: NA_PRIMARY,
                ...naReadableText,
              }}
            >
              Government &amp; public sector
            </h2>
            <div
              style={{
                width: "72px",
                height: "4px",
                borderRadius: "2px",
                marginBottom: "1.25rem",
                ...naGradientLine,
              }}
            />
            {GOVERNMENT.map((label, i) => (
              <AttendeeRow key={i} label={label} icon={Landmark} color={NA_PRIMARY} />
            ))}
          </Col>
          <Col lg={6}>
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "1.35rem" : "1.2rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: NA_SECONDARY,
                ...naReadableText,
              }}
            >
              Private sector
            </h2>
            <div
              style={{
                width: "72px",
                height: "4px",
                borderRadius: "2px",
                marginBottom: "1.25rem",
                ...naGradientLine,
              }}
            />
            {PRIVATE.map((label, i) => {
              const icons = [Building2, DraftingCompass, HardHat, Cpu, Map, Sparkles];
              const Icon = icons[i] || Building2;
              return <AttendeeRow key={i} label={label} icon={Icon} color={NA_SECONDARY} />;
            })}
          </Col>
        </Row>

        <Row className="mt-5">
          <Col>
            <h2
              className="mb-3"
              style={{
                fontSize: size ? "2rem" : "1.55rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: NA_PRIMARY,
                ...naReadableText,
              }}
            >
              Why you should attend
            </h2>
            <div
              style={{
                width: "72px",
                height: "4px",
                borderRadius: "2px",
                marginBottom: "1.35rem",
                ...naGradientLine,
              }}
            />
          </Col>
        </Row>
        <Row>
          {WHY_ATTEND.map((line, i) => {
            const icons = [Lightbulb, Users, Cpu, LineChart, Handshake, Target];
            const Icon = icons[i] || Sparkles;
            return (
              <Col md={6} key={i} className="mb-3">
                <Card
                  style={{
                    ...naDarkCardBase,
                    borderRadius: "14px",
                    padding: size ? "1.1rem 1.25rem" : "1rem 1.1rem",
                    height: "100%",
                    transition: "all 0.35s ease",
                  }}
                  onMouseEnter={(e) => {
                    const c = i % 2 === 0 ? NA_PRIMARY : NA_SECONDARY;
                    onDarkCardEnter(e, c);
                    e.currentTarget.style.transform = "translateY(-3px)";
                  }}
                  onMouseLeave={(e) => {
                    onDarkCardLeave(e);
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        minWidth: 44,
                        borderRadius: "12px",
                        background: `linear-gradient(145deg, ${NA_PRIMARY}28, ${NA_SECONDARY}18)`,
                        border: `2px solid ${NA_PRIMARY}45`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={22} strokeWidth={2} style={{ color: NA_SECONDARY }} />
                    </div>
                    <span
                      className="text-white"
                      style={{
                        fontSize: size ? "0.98rem" : "0.9rem",
                        fontWeight: 600,
                        lineHeight: 1.45,
                        ...naReadableText,
                      }}
                    >
                      {line}
                    </span>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </section>
  );
}

export default NAWhoWhyAttendSection;
